import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { ExternalTextSchema } from "contracts/boundaries/external-text";
import { MAP_GROUP_ARCHETYPE } from "../../enums/map";
import {
  identityMatches,
  pageMatches,
  pageStronglyMatches,
} from "./boundaries/estate-archetype.classifier";
import type { MapPage, MapVocabulary } from "./mapTypes";

type EstateArchetype = Exclude<MapVocabulary["kind"], "general">;

type ArchetypeEvidence = {
  kind: EstateArchetype;
  matchedPages: number;
  points: number;
};

const ARCHETYPES: EstateArchetype[] = [
  MAP_GROUP_ARCHETYPE.COMMERCE,
  MAP_GROUP_ARCHETYPE.EDITORIAL,
  MAP_GROUP_ARCHETYPE.SERVICE,
];

const VOCABULARY: Record<MapVocabulary["kind"], MapVocabulary> = {
  commerce: {
    kind: MAP_GROUP_ARCHETYPE.COMMERCE,
    organise: "Product families",
    pattern: "Product and guidance patterns",
    destination: "Purchase and support destinations",
    patternName: "product-guidance pattern",
  },
  editorial: {
    kind: MAP_GROUP_ARCHETYPE.EDITORIAL,
    organise: "News sections and topics",
    pattern: "Article and hub patterns",
    destination: "Related coverage and subscription",
    patternName: "article pattern",
  },
  service: {
    kind: MAP_GROUP_ARCHETYPE.SERVICE,
    organise: "Services and guidance",
    pattern: "Service and content patterns",
    destination: "Enquiry, application and support",
    patternName: "service-guidance pattern",
  },
  general: {
    kind: MAP_GROUP_ARCHETYPE.GENERAL,
    organise: "Content groups",
    pattern: "Page and content patterns",
    destination: "Related destinations",
    patternName: "content pattern",
  },
};

export function mapVocabulary(data: OverviewResponse): MapVocabulary {
  return VOCABULARY[classifyEstate(data)];
}

export function mapPatternName(
  vocabulary: MapVocabulary,
  pages: MapPage[],
): string {
  const editorialPages = pages.filter((page) =>
    pageMatches(MAP_GROUP_ARCHETYPE.EDITORIAL, page),
  );
  const required = Math.max(2, Math.ceil(pages.length * 0.6));
  return editorialPages.length >= required
    ? VOCABULARY.editorial.patternName
    : vocabulary.patternName;
}

function classifyEstate(data: OverviewResponse): MapVocabulary["kind"] {
  const identity = `${data.site.displayName ?? ""} ${data.site.title ?? ""} ${data.site.domain}`;
  const evidence = ARCHETYPES.map((kind) =>
    collectEvidence(kind, data.pages, identity),
  ).sort(
    (left, right) =>
      right.points - left.points ||
      right.matchedPages - left.matchedPages ||
      left.kind.localeCompare(right.kind),
  );
  const winner = evidence[0];
  const runnerUp = evidence[1];
  if (!winner || !runnerUp) return MAP_GROUP_ARCHETYPE.GENERAL;

  const requiredMatches = Math.max(2, Math.ceil(data.pages.length * 0.12));
  const requiredLead = Math.max(3, Math.ceil(winner.points * 0.2));
  if (
    winner.matchedPages < requiredMatches ||
    winner.points - runnerUp.points < requiredLead
  ) {
    return MAP_GROUP_ARCHETYPE.GENERAL;
  }
  return winner.kind;
}

function collectEvidence(
  kind: EstateArchetype,
  pages: MapPage[],
  identity: string,
): ArchetypeEvidence {
  let matchedPages = 0;
  let points = identityMatches(kind, ExternalTextSchema.parse(identity))
    ? 3
    : 0;
  for (const page of pages) {
    if (!pageMatches(kind, page)) continue;
    matchedPages += 1;
    points += 2;
    if (pageStronglyMatches(kind, page)) points += 2;
  }
  return { kind, matchedPages, points };
}
